import { useCallback, useEffect, useRef, useState } from "react";
import {
  MdBusiness,
  MdDelete,
  MdEdit,
  MdPublic,
  MdReportProblem,
  MdSync,
  MdWarningAmber,
} from "react-icons/md";
import { FieldGroup, Input, SelectField, Textarea } from "../../components/ui";
import { HabBadge, habColor } from "../../shared/habilitations";
import { risqueTraitementColor } from "../../shared/statusColors";
import HighlightText from "../../shared/HighlightText";
import { formatDateFr } from "../../shared/dateFormat";
import { COLORS, RADIUS, SPACING } from "../../shared/theme";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";
import { hapivotBibliothequeApi, ropaAiApi } from "../../services/apiClient";

function PrestataireLogo({ nom, logoUrl, size = 36 }) {
  const [failed, setFailed] = useState(false);
  const initials =
    (nom || "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?";
  const bg = "#0ea5e9";
  if (!logoUrl || failed) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: 8,
          background: bg,
          color: "#fff",
          fontSize: Math.max(10, size * 0.35),
          fontWeight: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {initials.slice(0, 2)}
      </div>
    );
  }
  return (
    <img
      src={logoUrl}
      alt=""
      width={size}
      height={size}
      style={{ borderRadius: 8, objectFit: "contain", background: "#f8fafc" }}
      onError={() => setFailed(true)}
    />
  );
}

const FINALITES = ["Gestion clients/prospects", "Marketing/Newsletter", "RH/Paie", "Comptabilité/Facturation", "Sécurité/Vidéosurveillance", "Support client", "Analytics web", "Gestion des achats", "R&D", "Autre"];
const BASES_LEGALES = [
  "Consentement (art. 6(1)(a))",
  "Contrat (art. 6(1)(b))",
  "Obligation légale (art. 6(1)(c))",
  "Sauvegarde intérêts vitaux (art. 6(1)(d))",
  "Mission intérêt public (art. 6(1)(e))",
  "Intérêt légitime (art. 6(1)(f))",
];
const CATEGORIES_DONNEES = ["Identité", "Coordonnées", "Données financières", "Vie professionnelle", "Données de connexion", "Données de localisation", "Données de santé (sensibles)", "Données judiciaires", "Données biométriques", "Opinions politiques/religieuses", "Autre"];
const CATEGORIES_PERSONNES = ["Clients", "Prospects", "Employés", "Candidats", "Fournisseurs", "Visiteurs", "Utilisateurs web", "Patients", "Adhérents", "Autre"];
const DESTINATAIRES_TYPES = ["Services internes", "Sous-traitants", "Partenaires commerciaux", "Autorités publiques", "Organismes sociaux", "Autre"];
const DUREES_CONSERVATION = ["< 1 an", "1-3 ans", "3-5 ans", "5-10 ans", "> 10 ans", "Durée du contrat + X ans", "Variable selon traitement"];
const MESURES_SECURITE = ["Chiffrement", "Contrôle d'accès", "Pseudonymisation", "Journalisation", "Sauvegardes", "Pare-feu", "Antivirus", "Formation utilisateurs", "Procédures internes"];

const emptyTraitement = {
  nom: "", finalite: "Gestion clients/prospects", baseLegale: "Contrat (art. 6(1)(b))", description: "",
  categoriesDonnees: [], categoriesPersonnes: [], originesDonnees: "",
  destinataires: [], destinatairesDetail: "",
  transfertsHorsUE: false, transfertsDetail: "",
  dureeConservation: "1-3 ans", dureeDetail: "",
  mesuresSecurite: [], mesuresDetail: "",
  responsableId: "", sousTraitantsConcernes: "",
  risque: "Modéré", piaRealise: false,
  dateCreation: "", derniereMaj: "", observations: "",
  hapivotPrestataireId: "",
  hapivotType: "",
  logoUrl: null,
  logoCachedPath: "",
};


export default function PageROPA({ traitements, setTraitements, personnes, sousTraitants, mode, expressAction, onClearExpressAction, searchHighlight, onTraitementsReload, onToast }) {
  const [showForm, setShowForm] = useState(false);
  const highlightRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("Tous");
  /** Ids négatifs = brouillon local ; le serveur attribue l’id définitif à la sync (évite collision avec $idx+1 côté API). */
  const nextClientId = useRef(-1);
  const [form, setForm] = useState(null);
  const [hapivotModal, setHapivotModal] = useState(false);
  const [hapivotQ, setHapivotQ] = useState("");
  const [hapivotCategory, setHapivotCategory] = useState("");
  const [hapivotPage, setHapivotPage] = useState(1);
  const [hapivotData, setHapivotData] = useState(null);
  const [hapivotLoading, setHapivotLoading] = useState(false);
  const [hapivotSelected, setHapivotSelected] = useState(() => new Set());
  const [hapivotImporting, setHapivotImporting] = useState(false);
  const [ropaAiUrl, setRopaAiUrl] = useState("");
  const [ropaAiLoading, setRopaAiLoading] = useState(false);
  const isExpress = mode === "express";
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setCheck = k => e => setForm(f => ({ ...f, [k]: e.target.checked }));
  const toggleArray = (k, val) => setForm(f => ({
    ...f,
    [k]: f[k].includes(val) ? f[k].filter(v => v !== val) : [...f[k], val]
  }));

  const openNew = useCallback(() => {
    setEditing(null);
    // Pré-remplissage exemple si mode Express
    if (isExpress) {
      setForm({
        ...emptyTraitement,
        nom: "Base de contact journalistes",
        finalite: "Marketing/Newsletter",
        baseLegale: "Intérêt légitime (art. 6(1)(f))",
        description: "Gestion des contacts journalistes pour envoi de communiqués de presse",
        categoriesDonnees: ["Identité", "Coordonnées"],
        categoriesPersonnes: ["Autre"],
        dureeConservation: "3-5 ans",
        dureeDetail: "3 ans après dernière interaction",
        transfertsHorsUE: false,
        mesuresSecurite: ["Contrôle d'accès"],
        responsableId: 1,
      });
    } else {
      setForm({ ...emptyTraitement });
    }
    setShowForm(true);
  }, [isExpress]);

  // Auto-open form when coming from Dashboard Express
  useEffect(() => {
    const shouldOpenRopaForm = expressAction?.action === "ropa" && !showForm;
    if (!shouldOpenRopaForm) return;

    openNew();
    if (onClearExpressAction) onClearExpressAction();
  }, [expressAction, onClearExpressAction, openNew, showForm]);
  const openEdit = t => { setEditing(t); setForm({ ...emptyTraitement, ...t }); setShowForm(true); };

  const loadHapivotCatalog = useCallback(async (page = 1, q = "", categorie = "") => {
    setHapivotLoading(true);
    try {
      const data = await hapivotBibliothequeApi.catalog({
        page: String(page),
        per_page: "20",
        q,
        categorie: categorie || undefined,
      });
      setHapivotData(data);
      setHapivotPage(page);
    } catch (e) {
      setHapivotData({ items: [], categories: [], total: 0, page: 1, per_page: 20 });
      onToast?.("error", e?.message || "Impossible de charger la bibliothèque pré configurée.");
    } finally {
      setHapivotLoading(false);
    }
  }, [onToast]);

  useEffect(() => {
    if (!hapivotModal) return;
    setHapivotSelected(new Set());
    setHapivotQ("");
    setHapivotCategory("");
    loadHapivotCatalog(1, "", "");
  }, [hapivotModal, loadHapivotCatalog]);

  const toggleHapivotSelect = (id) => {
    setHapivotSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < 25) {
        next.add(id);
      }
      return next;
    });
  };

  const runHapivotImport = async () => {
    if (hapivotSelected.size === 0) return;
    setHapivotImporting(true);
    try {
      const ids = [...hapivotSelected];
      const res = await hapivotBibliothequeApi.import(ids);
      const extra = res?.skipped?.length ? ` (${res.skipped.length} déjà présent(s))` : "";
      onToast?.("success", (res?.message || "Import terminé.") + extra);
      if (onTraitementsReload) await onTraitementsReload();
      setHapivotModal(false);
      setHapivotSelected(new Set());
    } catch (e) {
      onToast?.("error", e?.message || "Import impossible.");
    } finally {
      setHapivotImporting(false);
    }
  };
  const closeForm = useCallback(() => {
    setShowForm(false);
    setForm(null);
    setEditing(null);
    setRopaAiUrl("");
  }, []);

  const runRopaAiFill = useCallback(async () => {
    const u = ropaAiUrl.trim();
    if (!u) return;
    setRopaAiLoading(true);
    try {
      const res = await ropaAiApi.fillFromUrl(u);
      const patch = res?.fields && typeof res.fields === "object" ? res.fields : {};
      setForm((f) => {
        if (!f) return f;
        const next = { ...f };
        for (const key of Object.keys(patch)) {
          if (!Object.prototype.hasOwnProperty.call(emptyTraitement, key)) continue;
          if (key === "hapivotPrestataireId" || key === "hapivotType" || key === "logoUrl" || key === "logoCachedPath") continue;
          const v = patch[key];
          if (v === undefined || v === null) continue;
          next[key] = v;
        }
        // Logo depuis le domaine de l’URL (même logique cache / logo.dev / favicon que la bibliothèque)
        if (res?.sourceHost) {
          next.logoUrl = res.logoUrl ?? null;
          next.logoCachedPath = res.logoCachedPath ?? "";
          next.hapivotPrestataireId = "";
          next.hapivotType = "";
        }
        return next;
      });
      onToast?.("success", "Suggestion IA appliquée — contrôlez les champs avant enregistrement.");
    } catch (e) {
      onToast?.("error", e?.message || "Analyse IA impossible.");
    } finally {
      setRopaAiLoading(false);
    }
  }, [onToast, ropaAiUrl]);

  const save = () => {
    if (editing) setTraitements(ts => ts.map(t => t.id === editing.id ? { ...form, id: editing.id } : t));
    else setTraitements(ts => [...ts, { ...form, id: nextClientId.current-- }]);
    setShowForm(false);
    setEditing(null);
    setForm(null);
  };

  const pOptions = [{ value: "", label: "— Sélectionner —" }, ...personnes.map(p => ({ value: p.id, label: `${p.prenom} ${p.nom} (${p.habilitation})` }))];
  const filtered = filter === "Tous" ? traitements : filter === "PIA requis" ? traitements.filter(t => !t.piaRealise && (t.risque === "Élevé" || t.risque === "Critique")) : traitements.filter(t => t.finalite === filter);

  const rawHighlightId = searchHighlight?.id != null ? Number(searchHighlight.id) : null;
  const highlightItemType = searchHighlight?.itemType || null;
  const highlightId =
    rawHighlightId != null && (!highlightItemType || highlightItemType === "traitement") ? rawHighlightId : null;
  const searchTerm = searchHighlight?.searchTerm || null;

  useEffect(() => {
    if (highlightId != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId]);

  useEffect(() => {
    if (!showForm) return;
    window.scrollTo(0, 0);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showForm]);

  const stats = {
    total: traitements.length,
    horsUE: traitements.filter(t => t.transfertsHorsUE).length,
    piaManquants: traitements.filter(t => !t.piaRealise && (t.risque === "Élevé" || t.risque === "Critique")).length,
    sensibles: traitements.filter(t => t.categoriesDonnees.some(c => c.includes("sensibles") || c.includes("santé") || c.includes("judiciaires") || c.includes("biométriques"))).length,
  };

  return (
    <PageShell>
      <PageHeader
        title="ROPA — Registre des activités de traitement"
        subtitle="Article 30 RGPD — Document central de l'accountability"
        actions={
          <button type="button" className="page-shell__btn page-shell__btn--primary" onClick={() => setHapivotModal(true)}>
            + Nouveau traitement
          </button>
        }
      />

      <PageContent>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 22 }}>
          {[
            { label: "Total traitements", val: stats.total, color: "#0f172a", bg: "#f0f9ff", border: "#7dd3fc" },
            { label: "Transferts hors UE", val: stats.horsUE, color: "#991b1b", bg: "#fee2e2", border: "#fca5a5" },
            { label: "PIA manquants", val: stats.piaManquants, color: "#9d174d", bg: "#fdf2f8", border: "#f9a8d4" },
            { label: "Données sensibles", val: stats.sensibles, color: "#92400e", bg: "#fef3c7", border: "#fcd34d" },
          ].map(k => (
            <div key={k.label} style={{ background: k.bg, borderRadius: RADIUS.lg, padding: "14px 18px", border: `1.5px solid ${k.border}` }}>
              <div style={{ fontSize: 30, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.val}</div>
              <div style={{ fontSize: 12, color: k.color, marginTop: 3, fontWeight: 600 }}>{k.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 6, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: COLORS.textSecondary, fontWeight: 600 }}>Filtrer :</span>
          <button onClick={() => setFilter("Tous")} style={{ padding: "4px 12px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: filter === "Tous" ? "#0f172a" : "#fff", color: filter === "Tous" ? "#fff" : "#374151", border: `1.5px solid ${filter === "Tous" ? "#0f172a" : COLORS.border}` }}>Tous</button>
          <button onClick={() => setFilter("PIA requis")} style={{ padding: "4px 12px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: filter === "PIA requis" ? "#9d174d" : "#fff", color: filter === "PIA requis" ? "#fff" : "#374151", border: `1.5px solid ${filter === "PIA requis" ? "#9d174d" : COLORS.border}` }}>PIA requis</button>
          {FINALITES.slice(0, 6).map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ padding: "4px 12px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: filter === f ? "#0ea5e9" : "#fff", color: filter === f ? "#fff" : "#374151", border: `1.5px solid ${filter === f ? "#0ea5e9" : COLORS.border}` }}>{f}</button>
          ))}
        </div>

        {hapivotModal && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="nouveau-traitement-modal-title"
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
            onClick={() => !hapivotImporting && setHapivotModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ background: COLORS.white, borderRadius: RADIUS.xl, maxWidth: 720, width: "100%", maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 25px 50px rgba(0,0,0,0.2)" }}
            >
              <div style={{ padding: "18px 20px", borderBottom: `1.5px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <div id="nouveau-traitement-modal-title" style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>Nouveau traitement</div>
                  <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 4 }}>Créez une fiche vide ou importez jusqu’à 25 modèles pré remplis depuis la bibliothèque.</div>
                </div>
                <button type="button" disabled={hapivotImporting} onClick={() => setHapivotModal(false)} style={{ border: "none", background: "transparent", fontSize: 22, lineHeight: 1, cursor: "pointer", color: COLORS.textSecondary }} aria-label="Fermer">×</button>
              </div>
              <div style={{ padding: "10px 20px 6px", fontSize: 12, fontWeight: 700, color: "#0f172a", letterSpacing: "0.02em" }}>Traitements pré configurés</div>
              <div style={{ padding: "4px 20px 12px", fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.4 }}>Sélectionnez un ou plusieurs prestataires pour générer des entrées ROPA préremplies.</div>
              <div style={{ padding: "0 20px 12px", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", borderBottom: `1px solid ${COLORS.border}` }}>
                <select
                  value={hapivotCategory}
                  onChange={(e) => {
                    const v = e.target.value;
                    setHapivotCategory(v);
                    loadHapivotCatalog(1, hapivotQ, v);
                  }}
                  style={{ padding: "8px 12px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, fontSize: 13, minWidth: 160, background: COLORS.white }}
                  aria-label="Filtrer par catégorie"
                >
                  <option value="">Toutes les catégories</option>
                  {(hapivotData?.categories || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <input
                  value={hapivotQ}
                  onChange={(e) => setHapivotQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadHapivotCatalog(1, hapivotQ, hapivotCategory)}
                  placeholder="Rechercher (nom, id, catégorie)…"
                  style={{ flex: 1, minWidth: 200, padding: "8px 12px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, fontSize: 13 }}
                />
                <button type="button" onClick={() => loadHapivotCatalog(1, hapivotQ, hapivotCategory)} disabled={hapivotLoading} style={{ padding: "8px 16px", borderRadius: RADIUS.md, border: "none", background: "#0f172a", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Rechercher</button>
              </div>
              <div style={{ flex: 1, overflow: "auto", padding: "12px 20px" }}>
                {hapivotLoading && <div style={{ textAlign: "center", padding: 24, color: COLORS.textMuted }}>Chargement…</div>}
                {!hapivotLoading && hapivotData?.items?.length === 0 && (
                  <div style={{ textAlign: "center", padding: 24, color: COLORS.textMuted }}>Aucun résultat.</div>
                )}
                {!hapivotLoading && (hapivotData?.items || []).map((row) => (
                  <label
                    key={row.id}
                    style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 8px", borderRadius: RADIUS.md, border: `1px solid ${COLORS.border}`, marginBottom: 8, cursor: "pointer", background: hapivotSelected.has(row.id) ? "#f0f9ff" : COLORS.white }}
                  >
                    <input
                      type="checkbox"
                      checked={hapivotSelected.has(row.id)}
                      onChange={() => toggleHapivotSelect(row.id)}
                      style={{ marginTop: 4 }}
                    />
                    <PrestataireLogo nom={row.nom} logoUrl={row.logo_url} size={40} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.textPrimary }}>{row.nom}</div>
                      <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 4 }}>
                        <span style={{ fontFamily: "monospace" }}>{row.id}</span>
                        {" · "}{row.categorie}
                        {" · "}{row.type === "responsable_conjoint" ? "Responsable conjoint (art. 26)" : "Sous-traitant (art. 28)"}
                        {row.statut && (
                          <span style={{ marginLeft: 8, padding: "2px 8px", borderRadius: RADIUS.pill, background: row.statut === "verifie" ? "#d1fae5" : "#fef3c7", color: "#78350f", fontWeight: 600 }}>
                            {row.statut === "verifie" ? "Vérifié" : "À vérifier"}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <div style={{ padding: "12px 20px", borderTop: `1.5px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <div style={{ fontSize: 12, color: COLORS.textSecondary }}>
                  {hapivotData?.total != null ? `${hapivotData.total} prestataire(s)` : ""}
                  {hapivotSelected.size > 0 ? ` · ${hapivotSelected.size} sélectionné(s)` : ""}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    disabled={hapivotLoading || hapivotPage <= 1}
                    onClick={() => loadHapivotCatalog(hapivotPage - 1, hapivotQ, hapivotCategory)}
                    style={{ padding: "6px 12px", borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, cursor: hapivotPage <= 1 ? "not-allowed" : "pointer" }}
                  >
                    Précédent
                  </button>
                  <span style={{ fontSize: 12, color: COLORS.textSecondary }}>Page {hapivotPage}</span>
                  <button
                    type="button"
                    disabled={hapivotLoading || !hapivotData || hapivotPage * (hapivotData.per_page || 20) >= (hapivotData.total || 0)}
                    onClick={() => loadHapivotCatalog(hapivotPage + 1, hapivotQ, hapivotCategory)}
                    style={{ padding: "6px 12px", borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, cursor: "pointer" }}
                  >
                    Suivant
                  </button>
                  <button
                    type="button"
                    disabled={hapivotImporting || hapivotSelected.size === 0}
                    onClick={runHapivotImport}
                    style={{ padding: "8px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: "#fff", fontWeight: 700, cursor: hapivotSelected.size === 0 ? "not-allowed" : "pointer" }}
                  >
                    {hapivotImporting ? "Import…" : "Importer"}
                  </button>
                </div>
              </div>
              <div style={{ padding: "14px 20px", borderTop: `1.5px solid ${COLORS.border}`, background: "#f8fafc", flexShrink: 0 }}>
                <button
                  type="button"
                  disabled={hapivotImporting}
                  onClick={() => {
                    setHapivotModal(false);
                    openNew();
                  }}
                  style={{
                    width: "100%",
                    padding: "10px 16px",
                    borderRadius: RADIUS.md,
                    border: `1.5px solid ${COLORS.border}`,
                    background: COLORS.white,
                    color: "#0f172a",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: hapivotImporting ? "not-allowed" : "pointer",
                  }}
                >
                  Ajouter manuellement
                </button>
                <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 8, lineHeight: 1.45 }}>Sans modèle : vous renseignez tous les champs vous-même.</div>
              </div>
            </div>
          </div>
        )}

        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: COLORS.textMuted, fontSize: 14 }}>Aucun traitement enregistré.</div>
        ) : filtered.map(t => {
          const rc = risqueTraitementColor[t.risque] || risqueTraitementColor["Modéré"];
          const resp = personnes.find(p => p.id === t.responsableId || p.id === Number(t.responsableId));
          const hc = resp ? (habColor[resp.habilitation] || habColor["Autre"]) : null;
          const sensible = t.categoriesDonnees.some(c => c.includes("sensibles") || c.includes("santé") || c.includes("judiciaires") || c.includes("biométriques"));
          const isHighlighted = t.id === highlightId || Number(t.id) === highlightId;
          return (
            <div
              key={t.id}
              ref={isHighlighted ? highlightRef : null}
              style={{
                background: isHighlighted ? "#fffbeb" : COLORS.white,
                borderRadius: RADIUS.lg,
                border: `1.5px solid ${isHighlighted ? "#f59e0b" : sensible ? "#fca5a5" : COLORS.border}`,
                padding: 18,
                marginBottom: 12,
                boxShadow: isHighlighted ? "0 0 0 2px #fef3c7" : "0 1px 4px rgba(0,0,0,0.04)",
                transition: "box-shadow 0.2s, background 0.2s",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
                  <PrestataireLogo nom={t.nom} logoUrl={t.logoUrl ?? t.logo_url} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 14, color: COLORS.textPrimary, marginBottom: 5 }}>
                    <HighlightText text={t.nom} term={searchTerm} />
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    {t.hapivotPrestataireId ? (
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: RADIUS.pill, background: "#ecfeff", color: "#0e7490", border: "1px solid #67e8f9", fontWeight: 700 }}>
                        Pré configuré · {t.hapivotType === "responsable_conjoint" ? "Art. 26" : "Art. 28"}
                      </span>
                    ) : null}
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#f0f9ff", color: "#0c4a6e", border: "1px solid #7dd3fc", fontWeight: 600 }}><HighlightText text={t.finalite} term={searchTerm} /></span>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: rc.bg, color: rc.text, border: `1px solid ${rc.border}`, fontWeight: 700 }}>Risque {t.risque}</span>
                    {sensible && <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#fef2f2", color: "#991b1b", border: "1px solid #fca5a5", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}><MdReportProblem size={13} /> Données sensibles</span>}
                    {t.transfertsHorsUE && <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}><MdPublic size={13} /> Hors UE</span>}
                    {t.piaRealise && <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#d1fae5", color: "#065f46", border: "1px solid #6ee7b7", fontWeight: 700 }}>✓ PIA réalisé</span>}
                    {!t.piaRealise && (t.risque === "Élevé" || t.risque === "Critique") && <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#fdf2f8", color: "#9d174d", border: "1px solid #f9a8d4", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}><MdWarningAmber size={13} /> PIA requis</span>}
                  </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => openEdit(t)} style={{ padding: `${SPACING.xs}px 9px`, borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.primaryAccent}`, background: "transparent", color: COLORS.primaryAccent, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdEdit size={14} /></button>
                  <button onClick={() => setTraitements(ts => ts.filter(x => x.id !== t.id))} style={{ padding: "4px 9px", borderRadius: RADIUS.sm, border: "1.5px solid #fee2e2", background: "#fff5f5", color: "#dc2626", fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdDelete size={14} /></button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 8 }}><HighlightText text={t.description} term={searchTerm} /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 11, marginBottom: 8 }}>
                <div><strong style={{ color: "#374151" }}>Base légale :</strong> <span style={{ color: COLORS.textSecondary }}>{t.baseLegale}</span></div>
                <div><strong style={{ color: "#374151" }}>Conservation :</strong> <span style={{ color: COLORS.textSecondary }}>{t.dureeConservation} {t.dureeDetail && `— ${t.dureeDetail}`}</span></div>
                {t.categoriesDonnees.length > 0 && <div style={{ gridColumn: "span 2" }}><strong style={{ color: "#374151" }}>Données :</strong> <span style={{ color: COLORS.textSecondary }}>{t.categoriesDonnees.join(", ")}</span></div>}
                {t.categoriesPersonnes.length > 0 && <div style={{ gridColumn: "span 2" }}><strong style={{ color: "#374151" }}>Personnes :</strong> <span style={{ color: COLORS.textSecondary }}>{t.categoriesPersonnes.join(", ")}</span></div>}
                {t.mesuresSecurite.length > 0 && <div style={{ gridColumn: "span 2" }}><strong style={{ color: "#374151" }}>Sécurité :</strong> <span style={{ color: COLORS.textSecondary }}>{t.mesuresSecurite.join(", ")}</span></div>}
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11, alignItems: "center" }}>
                {resp && hc && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 22, height: 22, borderRadius: "50%", background: hc.bg, border: `1.5px solid ${hc.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: hc.text }}>{resp.prenom[0]}{resp.nom[0]}</div><span>{resp.prenom} {resp.nom}</span><HabBadge habilitation={resp.habilitation} /></div>}
                {t.sousTraitantsConcernes && <span style={{ color: "#7c3aed", display: "inline-flex", alignItems: "center", gap: 4 }}><MdBusiness size={13} /> {t.sousTraitantsConcernes}</span>}
                {t.derniereMaj && <span style={{ color: COLORS.textSecondary, display: "inline-flex", alignItems: "center", gap: 4 }}><MdSync size={13} /> MAJ {formatDateFr(t.derniereMaj)}</span>}
              </div>
            </div>
          );
        })}

      {showForm && form && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="ropa-traitement-form-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10050,
            background: "rgba(15,23,42,0.55)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "24px 16px",
            overflowY: "auto",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 960,
              marginBottom: 24,
              maxHeight: "calc(100vh - 48px)",
              background: COLORS.white,
              borderRadius: RADIUS.xl,
              border: "2px solid #7dd3fc",
              boxShadow: "0 25px 50px rgba(0,0,0,0.2)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "16px 20px", borderBottom: `1.5px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexShrink: 0 }}>
              <div>
                <div id="ropa-traitement-form-title" style={{ fontWeight: 800, color: "#0f172a", fontSize: 16 }}>
                  {editing ? "Modifier le traitement" : "Nouveau traitement ROPA (art. 30)"}
                </div>
                {isExpress && (
                  <span style={{ display: "inline-block", marginTop: 8, fontSize: 12, fontWeight: 600, color: "#92400e", background: "#fef3c7", padding: "4px 10px", borderRadius: RADIUS.pill }}>
                    Mode Express — 8 champs essentiels
                  </span>
                )}
              </div>
              <button type="button" onClick={closeForm} style={{ border: "none", background: "transparent", fontSize: 22, lineHeight: 1, cursor: "pointer", color: COLORS.textSecondary }} aria-label="Fermer le formulaire">
                ×
              </button>
            </div>
            <div style={{ padding: "12px 20px", borderBottom: `1px solid ${COLORS.border}`, background: "#f8fafc", flexShrink: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>Assistant IA (page web)</div>
              <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 10, lineHeight: 1.45 }}>
                Indiquez l’URL d’une politique de confidentialité ou d’une page descriptive : le serveur extrait le texte et appelle l’IA pour proposer un brouillon (à valider manuellement).
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <input
                  type="url"
                  value={ropaAiUrl}
                  onChange={(e) => setRopaAiUrl(e.target.value)}
                  placeholder="https://exemple.com/politique-confidentialite"
                  disabled={ropaAiLoading}
                  style={{ flex: 1, minWidth: 220, padding: "8px 12px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, fontSize: 13 }}
                  onKeyDown={(e) => e.key === "Enter" && !ropaAiLoading && ropaAiUrl.trim() && runRopaAiFill()}
                />
                <button
                  type="button"
                  disabled={ropaAiLoading || !ropaAiUrl.trim()}
                  onClick={runRopaAiFill}
                  style={{
                    padding: "8px 16px",
                    borderRadius: RADIUS.md,
                    border: "none",
                    background: ropaAiLoading || !ropaAiUrl.trim() ? "#94a3b8" : "#7c3aed",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: ropaAiLoading || !ropaAiUrl.trim() ? "not-allowed" : "pointer",
                  }}
                >
                  {ropaAiLoading ? "Analyse…" : "Remplir avec l’IA"}
                </button>
              </div>
            </div>
            <div style={{ padding: 24, overflowY: "auto", flex: 1, minHeight: 0 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {/* Champs TOUJOURS visibles */}
              <div style={{ gridColumn: "span 2" }}>
                <FieldGroup label="Nom du traitement"><Input value={form.nom} onChange={set("nom")} placeholder="Ex : Gestion de la relation client (CRM)" /></FieldGroup>
              </div>
              <FieldGroup label="Finalité principale"><SelectField value={form.finalite} onChange={set("finalite")} options={FINALITES} /></FieldGroup>
              <FieldGroup label="Base légale"><SelectField value={form.baseLegale} onChange={set("baseLegale")} options={BASES_LEGALES} /></FieldGroup>
              
              {!isExpress && (
                <div style={{ gridColumn: "span 2" }}>
                  <FieldGroup label="Description du traitement"><Textarea value={form.description} onChange={set("description")} rows={2} placeholder="Décrire les opérations réalisées, le cycle de vie des données…" /></FieldGroup>
                </div>
              )}

              <div style={{ gridColumn: "span 2" }}>
                <FieldGroup label="Catégories de données traitées">
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                    {(isExpress ? CATEGORIES_DONNEES.slice(0, 5) : CATEGORIES_DONNEES).map(c => {
                      const selected = form.categoriesDonnees.includes(c);
                      const isSensible = c.includes("sensibles") || c.includes("santé") || c.includes("judiciaires") || c.includes("biométriques");
                      return (
                        <button key={c} onClick={() => toggleArray("categoriesDonnees", c)} style={{ padding: "4px 10px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: selected ? (isSensible ? "#fef2f2" : "#f0f9ff") : "#f9fafb", color: selected ? (isSensible ? "#991b1b" : "#0c4a6e") : "#6b7280", border: `1.5px solid ${selected ? (isSensible ? "#fca5a5" : "#7dd3fc") : COLORS.border}` }}>
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </FieldGroup>
              </div>

              {!isExpress && (
                <>
                  <div style={{ gridColumn: "span 2" }}>
                    <FieldGroup label="Catégories de personnes concernées">
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                        {CATEGORIES_PERSONNES.map(c => {
                          const selected = form.categoriesPersonnes.includes(c);
                          return (
                            <button key={c} onClick={() => toggleArray("categoriesPersonnes", c)} style={{ padding: "4px 10px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: selected ? "#f0fdf4" : "#f9fafb", color: selected ? "#15803d" : "#6b7280", border: `1.5px solid ${selected ? "#86efac" : COLORS.border}` }}>
                              {c}
                            </button>
                          );
                        })}
                      </div>
                    </FieldGroup>
                  </div>

                  <div style={{ gridColumn: "span 2" }}>
                    <FieldGroup label="Origine des données"><Input value={form.originesDonnees} onChange={set("originesDonnees")} placeholder="Ex : Formulaires web, téléphone, partenaires…" /></FieldGroup>
                  </div>

                  <div style={{ gridColumn: "span 2" }}>
                    <FieldGroup label="Destinataires (types)">
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                        {DESTINATAIRES_TYPES.map(d => {
                          const selected = form.destinataires.includes(d);
                          return (
                            <button key={d} onClick={() => toggleArray("destinataires", d)} style={{ padding: "4px 10px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: selected ? "#fef3c7" : "#f9fafb", color: selected ? "#92400e" : "#6b7280", border: `1.5px solid ${selected ? "#fcd34d" : COLORS.border}` }}>
                              {d}
                            </button>
                          );
                        })}
                      </div>
                    </FieldGroup>
                  </div>

                  <div style={{ gridColumn: "span 2" }}>
                    <FieldGroup label="Destinataires (détail)"><Input value={form.destinatairesDetail} onChange={set("destinatairesDetail")} placeholder="Ex : Service commercial, Salesforce (CRM), Stripe (paiement)…" /></FieldGroup>
                  </div>
                </>
              )}

              <div style={{ gridColumn: "span 2" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: form.transfertsHorsUE ? "#fef2f2" : "#f0fdf4", borderRadius: RADIUS.md, border: `1.5px solid ${form.transfertsHorsUE ? "#fca5a5" : "#86efac"}` }}>
                  <input type="checkbox" checked={form.transfertsHorsUE} onChange={setCheck("transfertsHorsUE")} id="transfertsHorsUE" style={{ width: 16, height: 16, cursor: "pointer" }} />
                  <label htmlFor="transfertsHorsUE" style={{ fontSize: 13, fontWeight: 600, color: form.transfertsHorsUE ? "#991b1b" : "#065f46", cursor: "pointer" }}>
                    {form.transfertsHorsUE ? "Transferts de données hors UE/EEE" : "Aucun transfert hors UE/EEE"}
                  </label>
                </div>
              </div>

              {form.transfertsHorsUE && !isExpress && (
                <div style={{ gridColumn: "span 2" }}>
                  <FieldGroup label="Détail des transferts hors UE"><Textarea value={form.transfertsDetail} onChange={set("transfertsDetail")} rows={2} placeholder="Pays, destinataires, garanties (CCT, BCR, décision d'adéquation)…" /></FieldGroup>
                </div>
              )}

              <FieldGroup label="Durée de conservation"><SelectField value={form.dureeConservation} onChange={set("dureeConservation")} options={DUREES_CONSERVATION} /></FieldGroup>
              {!isExpress && <FieldGroup label="Détail durée"><Input value={form.dureeDetail} onChange={set("dureeDetail")} placeholder="Ex : 3 ans après dernière commande" /></FieldGroup>}

              <div style={{ gridColumn: isExpress ? "span 2" : "span 2" }}>
                <FieldGroup label="Mesures de sécurité">
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                    {(isExpress ? MESURES_SECURITE.slice(0, 3) : MESURES_SECURITE).map(m => {
                      const selected = form.mesuresSecurite.includes(m);
                      return (
                        <button key={m} onClick={() => toggleArray("mesuresSecurite", m)} style={{ padding: "4px 10px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: selected ? "#eff6ff" : "#f9fafb", color: selected ? "#1d4ed8" : "#6b7280", border: `1.5px solid ${selected ? "#bfdbfe" : COLORS.border}` }}>
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </FieldGroup>
              </div>

              {!isExpress && (
                <>
                  <div style={{ gridColumn: "span 2" }}>
                    <FieldGroup label="Détail mesures de sécurité"><Textarea value={form.mesuresDetail} onChange={set("mesuresDetail")} rows={2} placeholder="Précisions sur les mesures mises en œuvre…" /></FieldGroup>
                  </div>

                  <FieldGroup label="Responsable"><SelectField value={form.responsableId} onChange={set("responsableId")} options={pOptions} /></FieldGroup>
                  <FieldGroup label="Niveau de risque"><SelectField value={form.risque} onChange={set("risque")} options={["Faible","Modéré","Élevé","Critique"]} /></FieldGroup>

                  <FieldGroup label="Sous-traitants concernés">
                    <Input value={form.sousTraitantsConcernes} onChange={set("sousTraitantsConcernes")} placeholder="Ex : AWS, Cloudflare, hébergeur…" />
                    <div style={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.4, marginTop: 4 }}>
                      Libellés ou noms d’éditeurs (séparés par des virgules). L’URL de l’assistant IA ci-dessus sert à l’analyse : elle n’est pas recopiée ici ; indiquez-la en « Observations » si vous voulez la tracer.
                    </div>
                  </FieldGroup>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 22 }}>
                    <input type="checkbox" id="piaRealise" checked={form.piaRealise} onChange={setCheck("piaRealise")} />
                    <label htmlFor="piaRealise" style={{ fontSize: 13, cursor: "pointer" }}>PIA / DPIA réalisé</label>
                  </div>

                  <FieldGroup label="Date de création"><Input type="date" value={form.dateCreation} onChange={set("dateCreation")} /></FieldGroup>
                  <FieldGroup label="Dernière mise à jour"><Input type="date" value={form.derniereMaj} onChange={set("derniereMaj")} /></FieldGroup>

                  <div style={{ gridColumn: "span 2" }}>
                    <FieldGroup label="Observations"><Textarea value={form.observations} onChange={set("observations")} rows={2} placeholder="Notes complémentaires…" /></FieldGroup>
                  </div>
                </>
              )}
            </div>
            </div>
            <div style={{ padding: "14px 20px", borderTop: `1.5px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end", gap: 8, flexShrink: 0, background: "#fafafa" }}>
              <button type="button" onClick={closeForm} style={{ padding: "8px 18px", borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Annuler</button>
              <button type="button" onClick={save} style={{ padding: "8px 18px", borderRadius: RADIUS.sm, border: "none", background: "#0ea5e9", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      </PageContent>
    </PageShell>
  );
}
